import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defaults } from 'lodash';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { AuthClient } from '@purecloud/pc-ui-auth';
import { DiscoveryService } from '@shared/services/discovery.service';
import { EnvironmentHelperService } from './environment-helper.service';

/** ? */
@Injectable()
export class ApiService {
  public readonly clientId = '0f1541a4-2941-4463-8035-48f7fd0b0057';

  private readonly authClient = new AuthClient(this.clientId, window.location.origin);

  constructor(
    private readonly http: HttpClient,
    private readonly envHelper: EnvironmentHelperService,
    private readonly discoveryService: DiscoveryService
  ) {}

  public get<T = any>(url: string, params: any = {}): Observable<T> {
    return this.makeRequest<T>('get', url, undefined, { params });
  }
  public post<T = any>(url: string, data: any, options: any = {}): Observable<T> {
    return this.makeRequest<T>('post', url, data, options);
  }
  public put<T = any>(url: string, data: any, options: any = {}): Observable<T> {
    return this.makeRequest<T>('put', url, data, options);
  }
  public patch<T = any>(url: string, data: any, options: any = {}): Observable<T> {
    return this.makeRequest<T>('patch', url, data, options);
  }
  public delete<T = any>(url: string, options: any = {}): Observable<T> {
    return this.makeRequest<T>('delete', url, null, options);
  }

  private makeRequest<T = any>(
    verb: string,
    url: string,
    body: Record<string, any> = {},
    options: Record<string, any> = {}
  ): Observable<T> {
    return forkJoin([this.discoveryService.get(), this.authClient.getCurrentAuthToken()]).pipe(
      filter(([_, token]) => !!token), // Prevent calling the api if no token exists
      switchMap(([disco, token]) => {
        const targetUrl = !url.match(/^http/) ? (<string>disco['api.uri']) + url : url;
        const requestOptions = this.createRequestOptions(body, options, token);

        return this.http
          .request<T>(verb, targetUrl, requestOptions)
          .pipe(catchError((error) => this.handle401Error(token, error)));
      })
    );
  }

  private handle401Error(token: string, error: HttpErrorResponse): Observable<never> {
    if (error.status === 401) {
      this.authClient.notify401Response(token);
    }

    return throwError(error);
  }

  private createRequestOptions(
    body: Record<string, any>,
    options: Record<string, any>,
    token: string
  ) {
    const requestOptions = defaults(options, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Genesys-App': `admin_v3_webui/${this.envHelper.appVersion}`
      }
    });
    if (body) {
      requestOptions.body = body;
    }

    return requestOptions;
  }
}
